import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Mail, Loader2, ShieldCheck } from "lucide-react";
import { authApi } from "../../services/auth";

function cn(...x: Array<string | false | undefined | null>) {
  return x.filter(Boolean).join(" ");
}

function InputLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2">
      <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-brand-brown/46">
        {children}
      </p>
    </div>
  );
}

function FieldShell(props: {
  icon: React.ReactNode;
  children: React.ReactNode;
  error?: boolean;
}) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-[18px] border bg-white/76 backdrop-blur-sm transition",
        props.error
          ? "border-red-500/35 focus-within:border-red-500/55"
          : "border-brand-brown/10 hover:border-brand-brown/20 focus-within:border-brand-brown/28",
      )}
    >
      <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-brand-brown/38">
        {props.icon}
      </div>
      {props.children}
    </div>
  );
}

export default function ForgotPasswordPage() {
  const { t, i18n } = useTranslation();
  const isVN = i18n.language?.startsWith("vi");

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const text = {
    back: isVN ? "Quay lại" : "Back",
    brand: "OWL DREAM",
    kicker: isVN ? "Khôi phục mật khẩu" : "Password reset",
    title: isVN ? "Quên mật khẩu" : "Forgot password",
    desc: isVN
      ? "Nhập email để nhận liên kết đặt lại mật khẩu."
      : "Enter your email to receive a reset link.",
    sentTitle: isVN ? "Đã gửi liên kết" : "Reset link sent",
    sentDesc1: isVN ? "Nếu email tồn tại trong hệ thống, liên kết đặt lại mật khẩu đã được gửi tới" : "If this email exists in our system, a reset link has been sent to",
    sentDesc2: isVN ? "Vui lòng kiểm tra hộp thư đến và spam." : "Please check your inbox and spam folder.",
    emailLabel: isVN ? "Email" : "Email",
    emailPlaceholder: isVN ? "you@email.com" : "you@email.com",
    sending: isVN ? "Đang gửi..." : "Sending...",
    send: isVN ? "Gửi liên kết" : "Send reset link",
    backLogin: isVN ? "Quay lại đăng nhập" : "Back to login",
    footer: "OWL DREAM / AUTH",
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const em = email.trim();

    if (!em) {
      setError(t("forgot.errors.emailRequired"));
      return;
    }

    if (!/^\S+@\S+\.\S+$/.test(em)) {
      setError(t("forgot.errors.emailInvalid"));
      return;
    }

    try {
      setLoading(true);
      await authApi.forgotPassword(em.toLowerCase());
      setSent(true);
    } catch (err: any) {
      const apiMessage =
        err?.response?.data?.message || t("forgot.errors.unableToSend");
      setError(apiMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="h-screen overflow-hidden bg-[#f6efe4] text-brand-brown">
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(189,160,103,0.10),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(57,34,25,0.05),transparent_28%)]" />
        <div className="absolute left-[-8%] top-[14%] h-[260px] w-[260px] rounded-full bg-brand-gold/10 blur-3xl" />
        <div className="absolute bottom-[-10%] right-[-8%] h-[240px] w-[240px] rounded-full bg-brand-brown/8 blur-3xl" />
      </div>

      <div className="relative flex h-full flex-col">
        <header className="border-b border-brand-brown/10 bg-[#f6efe4]/82 backdrop-blur-xl">
          <div className="mx-auto flex h-[72px] w-full max-w-[1440px] items-center justify-between px-4 sm:px-6 lg:px-10">
            <Link
              to="/auth"
              className="inline-flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.22em] text-brand-brown/58 transition hover:text-brand-brown"
            >
              <ArrowLeft className="h-4 w-4" />
              {text.back}
            </Link>

            <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-brand-brown/38">
              {text.brand}
            </p>
          </div>
        </header>

        <section className="flex min-h-0 flex-1 items-center justify-center px-4 py-4 sm:px-6 lg:px-8">
          <div className="w-full max-w-[860px]">
            <div className="overflow-hidden rounded-[34px] border border-brand-brown/10 bg-white/72 shadow-[0_28px_90px_rgba(43,31,26,0.08)] backdrop-blur-xl">
              <div className="grid lg:grid-cols-[0.42fr_0.58fr]">
                <aside className="relative hidden border-r border-brand-brown/8 bg-[linear-gradient(135deg,rgba(255,255,255,0.78),rgba(246,239,228,0.92))] lg:block">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(205,170,111,0.10),transparent_26%),radial-gradient(circle_at_bottom_right,rgba(43,31,26,0.04),transparent_26%)]" />

                  <div className="relative flex min-h-[520px] h-full flex-col justify-between p-8 xl:p-10">
                    <div className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-brand-brown/10 bg-white/80 text-brand-gold shadow-[0_8px_24px_rgba(43,31,26,0.05)]">
                      <ShieldCheck className="h-4 w-4" />
                    </div>

                    <div className="max-w-[320px]">
                      <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-brand-brown/40">
                        {text.kicker}
                      </p>

                      <h2 className="mt-5 text-[38px] font-medium leading-[0.96] tracking-[0.01em] text-brand-brown xl:text-[48px]">
                        {isVN ? "Khôi phục.\nNhanh.\nGọn." : "Reset.\nClean.\nEasy."}
                      </h2>

                      <p className="mt-5 text-sm leading-7 text-brand-brown/58">
                        {isVN
                          ? "Một bước nhỏ để lấy lại quyền truy cập tài khoản."
                          : "A simple step to regain access to your account."}
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2.5">
                      <div className="inline-flex h-10 items-center rounded-full border border-brand-brown/10 bg-white/72 px-4 text-[10px] font-medium uppercase tracking-[0.16em] text-brand-brown/62">
                        Private access
                      </div>
                      <div className="inline-flex h-10 items-center rounded-full border border-brand-brown/10 bg-white/72 px-4 text-[10px] font-medium uppercase tracking-[0.16em] text-brand-brown/62">
                        Secure reset
                      </div>
                    </div>
                  </div>
                </aside>

                <div className="flex min-h-[520px] flex-col">
                  <div className="border-b border-brand-brown/8 px-5 py-5 sm:px-6 lg:px-7">
                    <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-brand-brown/40">
                      {text.kicker}
                    </p>

                    <div className="mt-3 flex items-end justify-between gap-4">
                      <div>
                        <h1 className="text-[30px] font-medium tracking-[0.01em] text-brand-brown sm:text-[36px]">
                          {sent ? text.sentTitle : text.title}
                        </h1>
                        <p className="mt-1.5 text-sm text-brand-brown/56">
                          {sent ? text.sentDesc2 : text.desc}
                        </p>
                      </div>

                      <div className="flex h-11 w-11 items-center justify-center rounded-full border border-brand-brown/10 bg-brand-ivory/72 text-brand-brown/58">
                        <Mail className="h-4 w-4" />
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 p-4 sm:p-5 lg:p-6">
                    <div className="h-full rounded-[26px] border border-brand-brown/8 bg-brand-ivory/78 p-4 sm:p-5">
                      {!sent ? (
                        <form onSubmit={onSubmit} className="space-y-4">
                          <div>
                            <InputLabel>{text.emailLabel}</InputLabel>

                            <FieldShell icon={<Mail className="h-4 w-4" />} error={!!error}>
                              <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                autoComplete="email"
                                placeholder={text.emailPlaceholder}
                                className="h-12 w-full bg-transparent pl-11 pr-4 text-[15px] text-brand-brown outline-none placeholder:text-brand-brown/28"
                              />
                            </FieldShell>

                            {error && (
                              <p className="mt-1.5 text-[12px] text-red-600/80">
                                {error}
                              </p>
                            )}
                          </div>

                          <button
                            type="submit"
                            disabled={loading}
                            className={cn(
                              "inline-flex h-11 w-full items-center justify-center gap-2 rounded-[18px] px-4 transition",
                              "text-[10px] font-medium uppercase tracking-[0.18em]",
                              loading
                                ? "bg-brand-brown/20 text-brand-ivory/70 cursor-not-allowed"
                                : "bg-brand-brown text-brand-ivory shadow-[0_12px_24px_rgba(43,31,26,0.14)] hover:opacity-95",
                            )}
                          >
                            {loading ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                {text.sending}
                              </>
                            ) : (
                              text.send
                            )}
                          </button>

                          <div className="rounded-[18px] border border-brand-brown/10 bg-white/46 px-4 py-3 text-center text-[13px] text-brand-brown/56">
                            <Link
                              to="/auth"
                              className="font-medium text-brand-brown transition hover:text-brand-gold"
                            >
                              {text.backLogin}
                            </Link>
                          </div>
                        </form>
                      ) : (
                        <div className="flex h-full flex-col justify-center space-y-4">
                          <div className="rounded-[18px] border border-brand-brown/10 bg-white/60 px-4 py-4">
                            <p className="text-[13px] leading-6 text-brand-brown/58">
                              {text.sentDesc1}{" "}
                              <span className="font-medium text-brand-brown">
                                {email}
                              </span>
                              .
                            </p>
                          </div>

                          <Link
                            to="/auth"
                            className="inline-flex h-11 items-center justify-center rounded-[18px] border border-brand-brown/10 bg-white/78 px-4 text-[10px] font-medium uppercase tracking-[0.18em] text-brand-brown/70 transition hover:border-brand-brown/22 hover:bg-brand-brown/5 hover:text-brand-brown"
                          >
                            {text.backLogin}
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="border-t border-brand-brown/8 px-5 py-3.5 sm:px-6 lg:px-7">
                    <p className="text-center text-[10px] font-medium uppercase tracking-[0.18em] text-brand-brown/34">
                      OWL DREAM / RESET
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}